import type { TFunction } from 'i18next';
import { Archive, Building2, Pencil, Undo2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useMatch } from 'react-router';
import { useActiveContextOptional } from '@/auth/ActiveContextProvider';
import { Button } from '@/components/ui/button';
import { RowActionsMenu, type RowAction } from '@/components/ui/row-actions-menu';
import { formatDate } from '@/lib/intl';
import { cn } from '@/lib/utils';
import { placeBasePath } from '../place/place';
import { PlaceIcon } from '../ui/PlaceIcon';
import type { Acts } from './use-organization-acts';
import type { OrganizationRow } from './use-organizations';

type Translate = TFunction<'administration'>;

/** `can.archive` false on an active row is one of the contract's two refusals; say which. */
function refusalReason(
  t: Translate,
  row: OrganizationRow,
  workingIn: string | null,
  lastActive: boolean,
): string | null {
  if (row.status === 'archived' || row.can.archive) return null;
  if (row.id === workingIn) return t('organizations.why.workingIn');
  return lastActive ? t('organizations.why.lastActive') : null;
}

function rowMenu(
  t: Translate,
  row: OrganizationRow,
  acts: Acts,
  onRename: () => void,
  busy: boolean,
): RowAction[] {
  return [
    ...(row.can.rename
      ? [
          {
            id: 'rename',
            label: t('organizations.actions.rename'),
            icon: Pencil,
            onSelect: onRename,
          },
        ]
      : []),
    ...(row.can.archive
      ? [
          {
            id: 'archive',
            label: t('organizations.actions.archive'),
            icon: Archive,
            onSelect: () => acts.archive(row),
            busy,
          },
        ]
      : []),
  ];
}

function RowButtons({
  row,
  acts,
  busy,
  menu,
}: {
  readonly row: OrganizationRow;
  readonly acts: Acts;
  readonly busy: boolean;
  readonly menu: RowAction[];
}) {
  const { t } = useTranslation('administration');
  const archived = row.status === 'archived';
  return (
    <span className="people-row-actions gap-1">
      {archived && row.can.restore ? (
        <Button variant="outline" size="sm" onClick={() => acts.restore(row)} disabled={busy}>
          <Undo2 />
          {t('organizations.actions.restore')}
        </Button>
      ) : null}
      {!archived && row.can.administer ? (
        <Button asChild variant="outline" size="sm">
          <Link to={placeBasePath({ kind: 'organization', id: row.id })}>
            {t('organizations.actions.administer')}
          </Link>
        </Button>
      ) : null}
      {menu.length > 0 ? (
        <RowActionsMenu
          actions={menu}
          label={t('organizations.actions.more', { name: row.name })}
        />
      ) : null}
    </span>
  );
}

export function OrganizationItem({
  row,
  lastActive,
  acts,
  onRename,
  cardHref,
}: {
  readonly row: OrganizationRow;
  readonly lastActive: boolean;
  readonly acts: Acts;
  readonly onRename: () => void;
  readonly cardHref: string;
}) {
  const { t } = useTranslation('administration');
  const workingIn = useActiveContextOptional()?.active?.organizationId ?? null;
  const archived = row.status === 'archived';
  const busy = acts.isPending(`archive:${row.id}`) || acts.isPending(`restore:${row.id}`);
  const selected = useMatch(cardHref.split('?')[0] ?? cardHref) !== null;
  const why = refusalReason(t, row, workingIn, lastActive);
  const menu = rowMenu(t, row, acts, onRename, busy);

  return (
    <div
      role="listitem"
      className={cn(
        'people-row place-row',
        archived && 'place-row-archived',
        selected && 'people-row-selected',
      )}
    >
      <Link
        to={cardHref}
        className="people-row-main"
        aria-current={selected ? 'page' : undefined}
        aria-label={t('organizations.card.open', { name: row.name })}
      >
        <PlaceIcon icon={Building2} muted={archived} />
        <span className="flex min-w-0 flex-col">
          <span className="people-row-name">{row.name}</span>
          <span className="people-row-sub">
            {archived && row.archivedAt !== null
              ? t('organizations.archivedOn', { date: formatDate(row.archivedAt) })
              : t('organizations.kind')}
          </span>
          {why === null ? null : <span className="place-row-why">{why}</span>}
        </span>
      </Link>
      <span className="people-row-status">
        <span className={cn('status-badge', !archived && 'success')}>
          {t(`organizations.status.${row.status}`)}
        </span>
      </span>
      <span className="place-row-count">
        <span className="place-row-number">
          {t('organizations.peopleCount', { count: row.people })}
        </span>
        {row.pendingInvitations > 0 ? (
          <span className="people-row-sub">
            {archived
              ? t('organizations.invitationsOnHold', { count: row.pendingInvitations })
              : t('organizations.invitations', { count: row.pendingInvitations })}
          </span>
        ) : null}
      </span>
      <RowButtons row={row} acts={acts} busy={busy} menu={menu} />
    </div>
  );
}
